/**
 * Who a list resolves to, as a typed structure.
 *
 * Never SQL and never a free string. A rule has to be readable by a builder,
 * explainable to somebody deciding whether to send, and safe to store from a
 * form — and a string is none of those. Every criterion is also negatable,
 * because exclusion is what forced this shape rather than a refinement of it: a
 * campaign collecting FOR the Rav must not mail the Rav.
 *
 * A staff-role criterion resolves to whoever HOLDS the role, so an exclusion
 * survives the role changing hands. That is the whole reason it is a role and
 * not a person picked from a list.
 */

import { ORG_KINDS, type OrgKind } from './org-kind'

/** One test against a person, at one org. */
export type MembershipStanding = 'active' | 'inactive' | 'prospective'

/**
 * Where a person sits in a household, and what they are called at the org.
 *
 * Both mirror a database enum — `person_role` and `staff_role` — and are
 * spelled out rather than left as strings, so a rule naming a role the app has
 * never heard of is refused at the edge. Typed as a string it is accepted,
 * stored, and matches nobody, which reads as an audience that has shrunk for
 * some reason about the roster.
 */
export type HouseholdRole = 'head' | 'spouse' | 'child' | 'guest' | 'other'
export type StaffRole = 'admin' | 'gabbai' | 'treasurer' | 'rov' | 'president'

/**
 * Every criterion takes a SET, and matches when any of its values does.
 *
 * The OR lives inside a term, never between them: "active or former members,
 * and not a gabbai" is what audiences actually are, and a rule somebody can
 * read at a glance is worth more than one that can express anything. A single
 * value was the omission that made "anyone on the roster" unsayable — dues
 * follow the debt rather than the standing, and with one value per term there
 * was no way to say so without inventing an `any` sentinel to explain.
 *
 * `person` was always a set. The rest were singular by oversight, not design.
 */
export type ListCriterion =
  | { kind: 'membership'; statuses: MembershipStanding[] }
  /**
   * Whether the household is a member in its own right, or on the roster
   * because it is tied to one that is.
   *
   * This is what "the real member list" means and it is not expressible any
   * other way: a related household has a head and an active membership just
   * like a primary one, so role and standing cannot tell them apart. Without
   * it, a list meant for the congregation reaches every guest and married
   * child on the roster beside them.
   */
  | { kind: 'householdKind'; householdKinds: ('member' | 'related')[] }
  | { kind: 'householdRole'; roles: HouseholdRole[] }
  | { kind: 'staffRole'; roles: StaffRole[] }
  | { kind: 'reachable'; channels: ('email' | 'sms')[] }
  | { kind: 'person'; personIds: string[] }

/** A criterion and whether it must match or must not. */
export type ListTerm = { criterion: ListCriterion; negate?: boolean }

/**
 * Every term must hold. One conjunction rather than a tree of and/or: a rule
 * somebody can read at a glance is worth more than one that can express
 * anything, and "these people, except those" is what audiences actually are.
 */
export type ListRule = { all: ListTerm[] }

export const EMPTY_RULE: ListRule = { all: [] }

/**
 * Lists the APPLICATION resolves by key, which therefore cannot be deleted.
 *
 * A list's `key` is what ties a feature to it: a statement is a billing notice,
 * so `billingListId` looks up `billing` by name and the label above it is free
 * to be renamed to anything the shul likes. That indirection is what makes
 * renaming safe — and it is exactly what makes DELETING unsafe, because the
 * lookup then resolves to nothing and statements stop going out.
 *
 * So this is not a "system list" flag on the record. It is the short list of
 * keys some feature depends on by name, kept beside the seeds it comes from,
 * and a key joins it only when code starts resolving it.
 */
export const CODE_REFERENCED_LIST_KEYS = ['billing'] as const

/** Everybody the org counts as a current member — what almost every list means. */
export const ACTIVE_MEMBERS: ListRule = {
  all: [{ criterion: { kind: 'membership', statuses: ['active'] } }],
}

/**
 * Anyone the org has a membership record for, whatever their standing.
 *
 * What BILLING reaches, and the reason a criterion takes a set: a former
 * member who still owes money must keep receiving statements, so dues follow
 * the debt rather than the standing. With one value per term this could not be
 * written down at all.
 */
export const ANYONE_ON_THE_ROSTER: ListRule = {
  all: [
    {
      criterion: {
        kind: 'membership',
        statuses: ['active', 'inactive', 'prospective'],
      },
    },
  ],
}

type SeededListWording = { label: string; description: string }

const ORG_NOUN: Record<OrgKind, string> = {
  shul: 'shul',
  school: 'school',
  chesed: 'organization',
  group: 'group',
}

function wordingFor(
  make: (noun: string) => SeededListWording,
): Record<OrgKind, SeededListWording> {
  // fromEntries types its keys as string; mapping ORG_KINDS covers every kind.
  return Object.fromEntries(
    ORG_KINDS.map((kind) => [kind, make(ORG_NOUN[kind])]),
  ) as Record<OrgKind, SeededListWording>
}

const updatesWording = wordingFor((noun) => ({
  label: `${noun.charAt(0).toUpperCase()}${noun.slice(1)} updates`,
  description: `General announcements from the ${noun}.`,
}))
const campaignsWording = wordingFor((noun) => ({
  label: 'Appeals',
  description: `Appeals for the ${noun}’s campaigns, and pledge reminders.`,
}))

/**
 * The catalog a new org is seeded from, filtered to the lists that fit its kind.
 *
 * Seeded from the topics the enum carried, so no existing send changes audience
 * on the day this lands — plus **Campaigns**, which has no counterpart in the
 * enum. Campaign messages currently ride the general list, and without one the
 * list-is-topic decision has nothing to point at.
 *
 * The labels and descriptions are member-facing: the preferences page shows
 * them, so they are written for the person reading, not for the office.
 */
export const SEEDED_LISTS: {
  key: string
  label: string
  description: string
  /** The kinds of org whose work this list describes. */
  kinds: OrgKind[]
  /** Member-facing words chosen for each kind of org. */
  wording?: Partial<Record<OrgKind, SeededListWording>>
  /** Billing cannot be muted — the one list somebody may not switch off. */
  mutable?: boolean
  /** How somebody who never answered is reached. */
  defaultChannel?: 'none' | 'email' | 'sms' | 'both'
  /**
   * Who it starts out reaching. Everything except billing starts matching
   * NOBODY, so a new org's lists reach exactly the people who ask for them —
   * writing a real rule stays a deliberate act per list rather than something
   * creating an org does on the org's behalf.
   */
  rule?: ListRule
}[] = [
  {
    key: 'billing',
    label: 'Billing',
    description: 'Statements, pledges and payment reminders.',
    kinds: ['shul', 'school', 'chesed', 'group'],
    mutable: false,
    // Dues notices reach anyone on the roster who never touched their
    // preferences — including a former member, because leaving does not settle
    // what they owe.
    rule: ANYONE_ON_THE_ROSTER,
  },
  {
    key: 'membership',
    label: 'Membership',
    description: 'Membership renewals and roster matters.',
    kinds: ['shul', 'group'],
  },
  {
    key: 'simcha',
    label: 'Simchas',
    description: 'Simchas, and the shul events around them.',
    kinds: ['shul'],
  },
  {
    key: 'org_updates',
    label: updatesWording.shul.label,
    description: updatesWording.shul.description,
    kinds: ['shul', 'school', 'chesed', 'group'],
    wording: updatesWording,
  },
  {
    key: 'members_only',
    label: 'Members only',
    description: 'Notices meant for members alone.',
    kinds: ['shul', 'group'],
  },
  {
    key: 'weekly_zmanim',
    label: 'Weekly zmanim',
    description: "This Shabbos's davening times, every week.",
    kinds: ['shul'],
  },
  {
    key: 'zmanim',
    label: 'Yom Tov & holiday zmanim',
    description: 'Davening times for Yom Tov and the chagim.',
    kinds: ['shul'],
  },
  {
    key: 'yizkor',
    label: 'Yizkor',
    description: 'Reminders before each Yizkor.',
    kinds: ['shul'],
  },
  {
    key: 'campaigns',
    label: campaignsWording.shul.label,
    description: campaignsWording.shul.description,
    kinds: ['shul', 'school', 'chesed', 'group'],
    wording: campaignsWording,
  },
]
